<template>
  <div v-if="shouldRenderPanel" class="sidebar-v2__nested-panel" :style="nestedPanelStyle">
    <transition :name="transitionName" mode="out-in" appear @after-leave="handleAfterLeave">
      <div v-if="isVisible" :key="currentLevel.stackKey" class="sidebar-v2__nested-level">
        <div class="sidebar-v2__nested-header">
          <button class="sidebar-v2__nested-back" :aria-label="$t('back')" @click="goBack">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <h3 class="sidebar-v2__nested-title">{{ currentLevel.title }}</h3>
        </div>

        <div ref="nestedScrollContainer" class="sidebar-v2__nested-content" @scroll="captureScroll">
          <component
            v-if="currentLevel.component"
            :is="currentLevel.component"
            v-bind="currentLevel.componentProps"
          />

          <template v-else>
            <button
              v-if="showSeeAllLink"
              class="sidebar-v2__see-all-link"
              @click="handleSeeAllClick"
            >
              {{ $t('seeAll') + (currentLevel?.title ? ' ' + currentLevel?.title : '') }}
            </button>

            <ul class="unstyled-list sidebar-v2__nested-list">
              <li v-for="item in currentItems" :key="item.id" class="sidebar-v2__nested-list-item">
                <button class="sidebar-v2__nested-item-button" @click="handleItemClick(item)">
                  <span class="sidebar-v2__nested-item-label">{{ item.label }}</span>
                  <font-awesome-icon
                    class="sidebar-v2__nested-item-chevron"
                    icon="chevron-right"
                    v-if="item.children && item.children.length && stack.length < maxNestedLevels"
                  />
                </button>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import usePlatform from '@/modules/platform/composables/usePlatform';
import MobileSidebarMetricsMixin from '../../mixins/MobileSidebarMetricsMixin';

export default {
  mixins: [MobileSidebarMetricsMixin],
  setup() {
    const { platform } = usePlatform();
    return { platform };
  },
  props: {
    maxNestedLevels: {
      type: Number,
      default: 2,
    },
    onItemAction: {
      type: Function,
      required: true,
    },
  },
  emits: ['visibility-change'],
  data() {
    return {
      stack: [],
      isClosing: false,
      transitionName: 'sidebar-v2__nested-slide-forward',
    };
  },
  computed: {
    isVisible() {
      return this.stack.length > 0;
    },
    shouldRenderPanel() {
      return this.isVisible || this.isClosing;
    },
    nestedPanelStyle() {
      return this.mobileSidebarStyle;
    },
    currentLevel() {
      return (
        this.stack[this.stack.length - 1] || {
          title: '',
          items: [],
          component: null,
          componentProps: {},
        }
      );
    },
    currentItems() {
      return this.currentLevel.items || [];
    },
    isLastLevel() {
      if (!this.stack.length || this.currentLevel.component) {
        return false;
      }

      const hasNavigableChildren = this.currentItems.some(
        item => item.children && item.children.length,
      );
      const canOpenAnotherLevel = this.stack.length < this.maxNestedLevels;

      return !canOpenAnotherLevel || !hasNavigableChildren;
    },
    showSeeAllLink() {
      return this.isLastLevel && !!this.currentLevel.seeAllItem;
    },
  },
  watch: {
    isVisible(isVisible) {
      if (!isVisible) {
        this.isClosing = true;
      }

      this.$emit('visibility-change', isVisible);
    },
  },
  methods: {
    handleAfterLeave() {
      if (!this.isVisible) {
        this.isClosing = false;
      }
    },
    openMenu(title, items = [], options = {}) {
      const hasMenuItems = items && items.length;
      const hasComponent = !!options.component;

      if ((!hasMenuItems && !hasComponent) || this.stack.length >= this.maxNestedLevels) {
        return;
      }

      this.updateMobileSidebarMetrics();
      this.captureScroll();
      this.transitionName = 'sidebar-v2__nested-slide-forward';
      this.stack.push({
        title,
        items,
        component: options.component || null,
        componentProps: options.componentProps || {},
        seeAllItem: options.seeAllItem || null,
        scrollTop: 0,
        stackKey: `${Date.now()}-${this.stack.length}-${title}`,
      });

      this.$nextTick(() => {
        this.restoreScroll();
      });
    },
    goBack() {
      if (!this.stack.length) {
        return;
      }

      this.captureScroll();
      this.transitionName = 'sidebar-v2__nested-slide-backward';
      this.stack.pop();

      this.$nextTick(() => {
        this.restoreScroll();
      });
    },
    async handleItemClick(item) {
      const hasChildren = item.children && item.children.length;
      const canOpenNextLevel = this.stack.length < this.maxNestedLevels;

      if (hasChildren && canOpenNextLevel) {
        this.openMenu(item.label, item.children, { seeAllItem: item });
        return;
      }
      try {
        await this.onItemAction(item);
        this.resetMenu();
      } catch (error) {
        console.error(error);
      }
    },
    async handleSeeAllClick() {
      if (!this.currentLevel.seeAllItem) {
        return;
      }

      try {
        await this.onItemAction(this.currentLevel.seeAllItem);
        this.resetMenu();
      } catch (error) {
        console.error(error);
      }
    },
    resetMenu() {
      this.stack = [];
      this.transitionName = 'sidebar-v2__nested-slide-forward';
      this.$nextTick(() => {
        const nestedContainer = this.$refs.nestedScrollContainer;
        if (nestedContainer) {
          nestedContainer.scrollTop = 0;
        }
      });
    },
    captureScroll() {
      if (!this.stack.length) {
        return;
      }

      const nestedContainer = this.$refs.nestedScrollContainer;
      if (!nestedContainer) {
        return;
      }

      this.currentLevel.scrollTop = nestedContainer.scrollTop || 0;
    },
    restoreScroll() {
      if (!this.stack.length) {
        return;
      }

      const nestedContainer = this.$refs.nestedScrollContainer;
      if (!nestedContainer) {
        return;
      }

      nestedContainer.scrollTop = this.currentLevel.scrollTop || 0;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.unstyled-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sidebar-v2__nested-panel {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw; /* default for small screens */
  height: 100vh;
  z-index: 10001;
  background: var(--light);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-v2__nested-level {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sidebar-v2__nested-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  background: var(--white, $white);
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.3);
}

.sidebar-v2__nested-back {
  position: absolute;
  left: 0.75rem; /* keep it on left */
  border: 0;
  background: transparent;
  color: var(--dark);
  width: 2.25rem;
  height: 2.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999rem;
}

.sidebar-v2__nested-title {
  margin: 0;
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 400;
  text-align: center;
  flex: 1;
  padding: 0 3rem; /* add padding to ensure title is centered even with back button */
}

.sidebar-v2__nested-content {
  margin: 0.75rem;
  overflow-y: auto;
  max-height: 100vh;
  /* Stop rubber-band scroll from chaining to the page behind the fixed overlay on iOS. */
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}

.sidebar-v2__see-all-link {
  border: 0;
  background: transparent;
  color: $link-hover-color;
  font-size: 1rem;
  line-height: 1.25;
  margin-bottom: 0.75rem;
  padding: 0;
  padding-left: 1rem;
  text-decoration: underline;
  text-align: left;
}

.sidebar-v2__nested-list {
  border-radius: 0.5rem;
  border: 0.5px solid var(--dark-lighter-5);
  background: var(--white, $white);
  padding: 0 1.25rem;
}

.sidebar-v2__nested-list-item {
  border-top: 1px solid var(--dark-lighter-5);

  transition: background-color 0.22s ease, transform 0.22s ease;

  &:first-child {
    border-top: 0;
  }
}

.sidebar-v2__nested-item-button {
  width: 100%;
  min-height: 3.125rem;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  padding: 0;

  &:active {
    background: var(--gray-100, $gray-100);
  }

  transition: transform 0.22s ease;
}

.sidebar-v2__nested-item-label {
  color: var(--dark);
}

.sidebar-v2__nested-item-chevron {
  color: var(--dark-lighter-5);
  width: 0.9rem;
}

@media (hover: hover) and (pointer: fine) {
  .sidebar-v2__nested-list-item:hover,
  .sidebar-v2__nested-list-item:focus-within {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .sidebar-v2__nested-list-item:first-child:hover,
  .sidebar-v2__nested-list-item:first-child:focus-within {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  .sidebar-v2__nested-list-item:hover .sidebar-v2__nested-item-button,
  .sidebar-v2__nested-list-item:focus-within .sidebar-v2__nested-item-button {
    transform: translateX(0.125rem);
  }
}

@media (hover: none), (pointer: coarse) {
  .sidebar-v2__nested-list-item:active {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .sidebar-v2__nested-list-item:first-child:active {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  .sidebar-v2__nested-list-item:active .sidebar-v2__nested-item-button {
    transform: scale(0.995);
  }
}

.sidebar-v2__nested-slide-forward-enter-active,
.sidebar-v2__nested-slide-forward-leave-active,
.sidebar-v2__nested-slide-backward-enter-active,
.sidebar-v2__nested-slide-backward-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.sidebar-v2__nested-slide-forward-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.sidebar-v2__nested-slide-backward-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.sidebar-v2__nested-slide-forward-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.sidebar-v2__nested-slide-backward-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Apply fixed width when screen is larger */
@media (min-width: 28.76rem) {
  .sidebar-v2__nested-panel {
    width: 405px;
  }
}
</style>
